import {
  buildStageMatchdays,
  detectInitialMatchday,
  getStageMatchdayKey,
  type MatchForMatchday,
} from '@/utils/matchdayHelpers';
import { parseLocalIsoDate, shiftIsoDate, toLocalIsoDate } from '@/utils/localDate';

/** View modes of the league season list (`/matches/:leagueId`). */
export const LEAGUE_MATCHES_MODES = {
  DATE: 'date',
  ROUND: 'round',
} as const;

export type LeagueMatchesMode = typeof LEAGUE_MATCHES_MODES[keyof typeof LEAGUE_MATCHES_MODES];

export const isLeagueMatchesMode = (value: unknown): value is LeagueMatchesMode =>
  value === LEAGUE_MATCHES_MODES.DATE || value === LEAGUE_MATCHES_MODES.ROUND;

/** One local calendar day of matches, kickoff-sorted. */
export interface DayGroup<M> {
  /** Local calendar day, `YYYY-MM-DD`. */
  key: string;
  matches: M[];
}

/** One round (stage + matchday, or a standalone stage) split into local days. */
export interface RoundGroup<M> {
  /** `buildStageMatchdays` id, e.g. `Regular Season - 12-12` or `Quarter-finals`. */
  key: string;
  label: string;
  matchCount: number;
  days: DayGroup<M>[];
}

export interface RoundGrouping<M> {
  rounds: RoundGroup<M>[];
  /** Round to open the list on (`detectInitialMatchday`), or null without rounds. */
  initialKey: string | null;
}

type Datable = { id: number; match_date: string };

const byKickoff = (a: Datable, b: Datable): number =>
  Date.parse(a.match_date) - Date.parse(b.match_date) || a.id - b.id;

/** Local-day buckets of already kickoff-sorted matches, in input order. */
function bucketByLocalDay<M extends Datable>(sorted: readonly M[]): DayGroup<M>[] {
  const days: DayGroup<M>[] = [];
  for (const match of sorted) {
    const key = toLocalIsoDate(new Date(match.match_date));
    const last = days[days.length - 1];
    if (last && last.key === key) last.matches.push(match);
    else days.push({ key, matches: [match] });
  }
  return days;
}

/**
 * Matches bucketed by the user's LOCAL calendar day (not UTC), days ascending,
 * matches inside a day sorted by kickoff (ties by id, so the order is stable).
 */
export function groupMatchesByLocalDay<M extends Datable>(matches: readonly M[]): DayGroup<M>[] {
  return bucketByLocalDay([...matches].sort(byKickoff));
}

/**
 * Matches grouped into rounds in season order (`buildStageMatchdays`), each
 * round split into local-day sub-groups. `initialKey` is the round the list
 * anchors to on open.
 */
export function groupMatchesByRound<M extends MatchForMatchday>(matches: M[]): RoundGrouping<M> {
  const stageMatchdays = buildStageMatchdays(matches);

  const byRound = new Map<string, M[]>();
  for (const match of matches) {
    const key = getStageMatchdayKey(match);
    const bucket = byRound.get(key);
    if (bucket) bucket.push(match);
    else byRound.set(key, [match]);
  }

  const rounds = stageMatchdays.map(round => {
    const roundMatches = byRound.get(round.id) ?? [];
    return {
      key: round.id,
      label: round.label,
      matchCount: roundMatches.length,
      days: groupMatchesByLocalDay(roundMatches),
    };
  });

  return { rounds, initialKey: detectInitialMatchday(stageMatchdays, []) };
}

/**
 * Day the date list opens on: today when it has matches, otherwise the next
 * match day after today, otherwise the last match day of the season.
 */
export function pickAnchorDayKey(dayKeys: readonly string[], todayKey: string): string | null {
  if (dayKeys.length === 0) return null;
  // `YYYY-MM-DD` strings compare chronologically.
  return dayKeys.find(key => key >= todayKey) ?? dayKeys[dayKeys.length - 1];
}

export interface RelativeDayLabels {
  today: string;
  tomorrow: string;
  yesterday: string;
}

export type DayLabelStyle = 'long' | 'short';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(locale: string, style: DayLabelStyle, withYear: boolean): Intl.DateTimeFormat {
  const cacheKey = `${locale}|${style}|${withYear}`;
  let formatter = formatterCache.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, {
      weekday: style,
      day: 'numeric',
      month: style,
      ...(withYear ? { year: 'numeric' as const } : {}),
    });
    formatterCache.set(cacheKey, formatter);
  }
  return formatter;
}

const capitalize = (value: string): string =>
  value.charAt(0).toLocaleUpperCase() + value.slice(1);

/**
 * Header label of a local day: "Today" / "Tomorrow" / "Yesterday", otherwise
 * `Weekday, D Month` in the UI language — long ("Tuesday, 29 September",
 * «Вторник, 29 сентября») or short ("Tue, 29 Sep", «Вт, 29 сент.»). The year
 * is appended only when it differs from today's.
 */
export function formatDayLabel(
  dayKey: string,
  todayKey: string,
  locale: string,
  style: DayLabelStyle,
  relative: RelativeDayLabels,
): string {
  if (dayKey === todayKey) return relative.today;
  if (dayKey === shiftIsoDate(todayKey, 1)) return relative.tomorrow;
  if (dayKey === shiftIsoDate(todayKey, -1)) return relative.yesterday;

  const withYear = dayKey.slice(0, 4) !== todayKey.slice(0, 4);
  const parts = getFormatter(locale, style, withYear).formatToParts(parseLocalIsoDate(dayKey));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? '';

  const label = `${part('weekday')}, ${part('day')} ${part('month')}${withYear ? ` ${part('year')}` : ''}`;
  return capitalize(label);
}
